import { Button2, Button1, Select, TagInput } from "../../../index";
import reportIcon from "../../../../assets/icons/report-icon.png";
import searchIcon from "../../../../assets/icons/search-icon.png";
import profilePic from "../../../../assets/icons/Ellipse 81.png";
import crossIcon from "../../../../assets/icons/cross-Icon.png";
import { DatePicker, Input, Select as AntSelect, Switch } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import BillPaymentReview from "./BillPaymentReview";
import { billsApi, transfersApi, scheduledTransfersApi } from "../../../../api/endpoints";
import { newIdempotencyKey } from "../../../../utils/idempotency";
import TransferOtpModal from "../../../modals/TransferOtpModal";

const CATEGORIES = ["Utilities", "Tax", "Telecom", "Insurance", "Other"];
const CURRENCIES = ["AUD", "USD", "EUR", "GBP", "PKR"];

const BillPayment = ({ sourceAccountId, sourceAccount }) => {
  const [step, setStep] = useState(0);
  const [billers, setBillers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBillerId, setSelectedBillerId] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [billCurrency, setBillCurrency] = useState(sourceAccount?.currency || "AUD");

  const [form, setForm] = useState({
    crn: "",
    billAmount: "",
    transferDate: null,
    category: "",
    scheduleDate: null,
    scheduleRepeat: null,
    scheduleEndDate: null,
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    setBillCurrency(sourceAccount?.currency || "AUD");
  }, [sourceAccount?.currency]);

  useEffect(() => {
    billsApi
      .listBillers()
      .then((data) => setBillers(Array.isArray(data) ? data : []))
      .catch(() => setBillers([]));
  }, []);

  // Debounced bill-payment quote, shows fee preview before user confirms
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (
      !sourceAccountId ||
      !form.billAmount ||
      isNaN(Number(form.billAmount)) ||
      Number(form.billAmount) <= 0
    ) {
      setQuote(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      transfersApi
        .quote({
          sourceAccountId,
          sourceAmount: Number(form.billAmount),
          destCurrency: billCurrency,
          transferType: "BILL_PAYMENT",
        })
        .then(setQuote)
        .catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [sourceAccountId, billCurrency, form.billAmount]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return billers.filter(
      (b) =>
        (b.name || "").toLowerCase().includes(q) ||
        (b.email || "").toLowerCase().includes(q) ||
        (b.code || "").toLowerCase().includes(q)
    );
  }, [billers, searchTerm]);

  const selectedBiller = billers.find((b) => b.id === selectedBillerId);

  // Confirm button: scheduled bills go straight through (executed later by
  // the worker); immediate payments require the emailed OTP/PIN first.
  const handleConfirm = () => {
    setError(null);
    if (!sourceAccountId) {
      setError("Please select a source account first");
      return;
    }
    if (!form.billAmount || Number(form.billAmount) <= 0) {
      setError("Enter a valid bill amount");
      return;
    }
    if (!selectedBillerId && !form.crn) {
      setError("Please select a biller or enter a CRN");
      return;
    }
    if (isScheduled && form.scheduleDate) {
      handleCreate();
    } else {
      setOtpOpen(true);
    }
  };

  const handleCreate = async ({ otpCode, pinCode } = {}) => {
    setError(null);
    setSubmitting(true);
    try {
      const reference = `Bill: ${
        selectedBiller?.name || "Manual"
      } CRN:${form.crn || "-"}`;

      if (isScheduled && form.scheduleDate) {
        await scheduledTransfersApi.create({
          sourceAccountId,
          sourceAmount: Number(form.billAmount),
          destCurrency: billCurrency,
          transferType: "BILL_PAYMENT",
          beneficiaryId: selectedBillerId || undefined,
          category: form.category || undefined,
          referenceNote: reference,
          startDate: form.scheduleDate,
          frequency: form.scheduleRepeat || "MONTHLY",
          endDate: form.scheduleEndDate || undefined,
        });
      } else {
        await transfersApi.create({
          sourceAccountId,
          sourceAmount: Number(form.billAmount),
          destCurrency: billCurrency,
          transferType: "BILL_PAYMENT",
          beneficiaryId: selectedBillerId || undefined,
          category: form.category || undefined,
          referenceNote: reference,
          otpCode,
          pinCode,
        }, idempotencyKey);
      }

      setOtpOpen(false);
      setStep(2);
    } catch (e) {
      // OTP/PIN problems stay inside the modal so the user can retry/resend.
      const apiErr = e.response?.data?.error || "";
      if (apiErr.startsWith("OTP_") || apiErr.startsWith("PIN_")) throw e;
      setOtpOpen(false);
      setError(
        e.response?.data?.error ||
          e.response?.data?.details?.[0]?.message ||
          e.message ||
          "Bill payment failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 1) {
    return (
      <>
        <BillPaymentReview
          sourceAccount={sourceAccount}
          form={form}
          biller={selectedBiller}
          quote={quote}
          submitting={submitting}
          error={error}
          onConfirm={handleConfirm}
          onClick={setStep}
        />
        <TransferOtpModal
          open={otpOpen}
          onClose={() => setOtpOpen(false)}
          onVerify={handleCreate}
        />
      </>
    );
  }

  if (step === 2) {
    return (
      <div className="col-12 p-5 d-flex flex-column align-items-center gap-3">
        <span className="fs-3 fw-bold" style={{ color: "#1bbb6b" }}>
          ✓ Bill {isScheduled ? "scheduled" : "paid"}
        </span>
        <span className="fs14px text-muted">
          Bill payment of {form.billAmount}{" "}
          {billCurrency} processed.
        </span>
        <Button1
          buttonName={"View transactions"}
          className="btn-dark"
          onClick={() => (window.location.href = "/my-transaction")}
        />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <span className="fs-5 fw-medium">Bills Payment</span>
        <Button2
          buttonName={"Request a Biller"}
          className={"btn-outline-success "}
          divClassName={"gap-2 fs12px"}
          icon={<img src={reportIcon} alt="" />}
        />
      </div>

      <div className="col-lg-5 p-3">
        <div className="d-flex flex-column gap-3">
          <div className="d-flex flex-column gap-3">
            <Input
              placeholder="Search billers"
              className="p-3"
              style={{ borderColor: "#1bbb6b" }}
              prefix={<img src={searchIcon} alt="search" width={18} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
            <div
              className="d-flex flex-column gap-3 overflow-auto"
              style={{ maxHeight: "150px" }}
            >
              {filtered.length === 0 && searchTerm && (
                <span className="fs12px text-muted">
                  No billers found
                </span>
              )}
              {filtered.map((b) => (
                <div
                  key={b.id}
                  className="d-flex justify-content-between align-items-center cursor-pointer"
                  onClick={() => {
                    setSelectedBillerId(b.id);
                    setSearchTerm("");
                  }}
                >
                  <div className="d-flex gap-2 ">
                    <div className="rounded-circle">
                      <img
                        className="rounded-circle"
                        src={profilePic}
                        alt="biller"
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="d-flex flex-column ">
                      <span className="fs14px fw-medium">{b.name}</span>
                      <span className="fs12px" style={{ color: "#7D7D7D" }}>
                        {b.email || b.code || "biller"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {selectedBiller && (
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2 ">
                    <div className="rounded-circle">
                      <img
                        className="rounded-circle"
                        src={profilePic}
                        alt="biller"
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="d-flex flex-column ">
                      <span className="fs14px fw-medium">
                        {selectedBiller.name}
                      </span>
                      <span className="fs12px" style={{ color: "#1bbb6b" }}>
                        Selected
                      </span>
                    </div>
                  </div>
                  <div>
                    <img
                      src={crossIcon}
                      alt="cross"
                      className="cursor-pointer"
                      onClick={() => setSelectedBillerId(null)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex flex-column gap-2 mt-2">
            <label className="fs12px">Customer/Bill Reference Number</label>
            <Input
              className="p-3"
              placeholder="CRN"
              value={form.crn}
              onChange={(e) => update("crn", e.target.value)}
            />
          </div>

          <div className="d-flex gap-3 ">
            <div className="d-flex flex-column gap-2 w-75 ">
              <label className="fs12px">Bill amount</label>
              <Input
                className="p-3 w-100"
                placeholder="Bill amount"
                value={form.billAmount}
                onChange={(e) => update("billAmount", e.target.value)}
              />
            </div>
            <div className="d-flex flex-column gap-2 new-transfer-currency-field">
              <label className="fs12px">Currency</label>
              <AntSelect
                value={billCurrency}
                onChange={setBillCurrency}
                options={CURRENCIES.map((currency) => ({
                  label: currency,
                  value: currency,
                }))}
                className="new-transfer-currency-select"
                style={{ width: "100%" }}
                popupClassName="new-transfer-currency-popup"
              />
            </div>
          </div>
          {sourceAccount && (
            <span className="fs12px">
              Available:{" "}
              {Number(sourceAccount.availableBalance).toLocaleString()}{" "}
              {sourceAccount.currency}
            </span>
          )}

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Transfer date</label>
            <DatePicker
              className="p-3"
              placeholder="Select date"
              onChange={(d) =>
                update("transferDate", d?.toISOString() || null)
              }
            />
          </div>
        </div>
      </div>

      <div className="col-lg-6 p-3">
        <div className="d-flex flex-column gap-3">
          <div className="d-flex flex-column gap-2 ">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fs-5 fw-bold">
                Schedule or set recurring transfer
              </span>
              <Switch
                onChange={setIsScheduled}
                checked={isScheduled}
              />
            </div>

            {isScheduled && (
              <div className="d-flex flex-column gap-3 mt-4">
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">Date</label>
                  <DatePicker
                    placeholder="Pick start date"
                    className="p-3"
                    onChange={(d) =>
                      update("scheduleDate", d?.toISOString() || null)
                    }
                  />
                </div>
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">Repeat</label>
                  <Select
                    value={form.scheduleRepeat}
                    options={[
                      { name: "Weekly", value: "WEEKLY" },
                      { name: "Monthly", value: "MONTHLY" },
                      { name: "Yearly", value: "YEARLY" },
                    ]}
                    defaultValue="Choose frequency"
                    className="p-3"
                    onChange={(e) => update("scheduleRepeat", e.target.value)}
                  />
                </div>
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">End date</label>
                  <DatePicker
                    placeholder="Pick end date"
                    className="p-3"
                    onChange={(d) =>
                      update("scheduleEndDate", d?.toISOString() || null)
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <div className="d-flex flex-column gap-3 mt-3">
            <span className="fs-5 fw-bold">Additional Details</span>
            <div className="d-flex flex-column gap-2 ">
              <label className="fs12px ">Category</label>
              <Select
                value={form.category}
                options={CATEGORIES.map((c) => ({ name: c, value: c }))}
                defaultValue="Choose Category"
                className="rounded-2 p-3"
                onChange={(e) => update("category", e.target.value)}
              />
            </div>
            <div className="d-flex flex-column gap-2 ">
              <label className="fs12px ">Tags</label>
              <TagInput
                className="border py-3 "
                inputBgColor="transparent"
                tagColor="#1bbb6b"
                tagTextColor="#000"
                limit={10}
                placeholder="Add up to 10 Tags"
              />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="col-12 p-2">
          <div className="fs12px" style={{ color: "#FC555B" }}>
            {error}
          </div>
        </div>
      )}

      <div className=" d-flex justify-content-md-end justify-content-between p-2 gap-3">
        <Button1
          buttonName={"Save as Draft"}
          className={"btn-outline-success"}
        />
        <Button1
          buttonName={"Continue"}
          className="btn-dark "
          onClick={() => setStep(1)}
        />
      </div>
    </>
  );
};

export default BillPayment;


